import { MutableRefObject, useCallback, useState } from 'react';

import { useSessionClusterJobsMutation } from '@/Pages/ClusterJob/hooks/useSessionClusterJobs';
import useToast from '@/components/Toast/useToast';

import { useUpdateInsightsJobConfig } from '../components/SavedConfigurations/hooks/useInsightsJobConfigs';
import { InsightsJobFormData } from '../types';
import { convertFormDataToConfig } from '../utils/formatUtils';
import { useValidateForm } from './useValidateForm';

export const useSubmitForm = ({
  sessionId,
  formData,
  setErrors,
  onSuccess,
  onClose,
  resetForm,
  openSecretsRef,
  missingSecrets,
  selectedConfigId,
}: {
  sessionId: string;
  formData: InsightsJobFormData;
  setErrors: (
    errors: Partial<Record<keyof InsightsJobFormData, string>>
  ) => void;
  onSuccess: () => void;
  onClose: () => void;
  resetForm: () => void;
  openSecretsRef: MutableRefObject<(() => void) | null>;
  missingSecrets: () => boolean;
  selectedConfigId?: string;
}) => {
  const { createToast } = useToast();
  const { trigger: submit, isMutating: isSubmitting } =
    useSessionClusterJobsMutation(sessionId);

  const { validateForm } = useValidateForm({
    formData,
    setErrors,
  });

  const {
    trigger: updateInsightsJobConfig,
    isMutating: isUpdatingInsightsJobConfig,
  } = useUpdateInsightsJobConfig({
    sessionId: sessionId ?? '',
    configId: selectedConfigId,
  });

  const [erroredUpdateConfig, setErroredUpdateConfig] = useState(false);

  const submitForm = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    if (missingSecrets()) {
      openSecretsRef.current?.();
      return;
    }

    const requestData = convertFormDataToConfig(formData);

    try {
      await updateInsightsJobConfig({ json: { config: requestData } });
    } catch (error) {
      createToast({
        title: 'Failed to update insights job config',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
      setErroredUpdateConfig(true);
    }

    try {
      await submit({ json: requestData });

      createToast({
        title: `Insights job created${
          erroredUpdateConfig ? '' : ' and configuration updated'
        }`,
        description:
          'Your insights job has been queued and will start processing soon.',
      });

      onSuccess?.();
      onClose();

      resetForm();
    } catch (error) {
      createToast({
        title: 'Failed to create insights job',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  }, [
    formData,
    validateForm,
    submit,
    missingSecrets,
    openSecretsRef,
    updateInsightsJobConfig,
    createToast,
    onSuccess,
    onClose,
    resetForm,
    erroredUpdateConfig,
    setErroredUpdateConfig,
  ]);

  return {
    submitForm,
    isSubmitting: isSubmitting || isUpdatingInsightsJobConfig,
  };
};
